import { SaveOutlined } from "@ant-design/icons";
import { App, Button, Drawer, Form, Modal, Space } from "antd";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { createDraft, submitDraft, updateDraft, type EntryDraftRecord } from "../../api/draftApi";
import {
  createBillEntry,
  getBillBankAccountBalance,
  getBillEntry,
  getBillPartyBalance,
  getPendingBills,
  previewBillEntry,
  updateBillEntry
} from "../../api/entryApi";
import { AttachmentPanel } from "../../shared/components/AttachmentPanel";
import { VoucherHeaderForm } from "./VoucherHeaderForm";
import { PendingBillsAllocationCard, VoucherInvoiceLines } from "./VoucherLineSections";
import {
  balanceText,
  buildPayload,
  extractId,
  hydrateDraftValues,
  newLine,
  serializeDraftValues,
  validateOverdraftPayment,
  validateVoucher,
  type AllocationRow,
  type BalanceInfo,
  type LineRow,
  type PendingBill,
  type PreviewInfo,
  type VoucherKind
} from "./voucherEntryUtils";

export function VoucherEntryDrawer({
  open,
  onClose,
  voucherType,
  title,
  editId,
  onSaved,
  draft,
  onDraftSaved
}: {
  open: boolean;
  onClose: () => void;
  voucherType: VoucherKind;
  title: string;
  editId?: number | string | null;
  onSaved: () => void;
  draft?: EntryDraftRecord | null;
  onDraftSaved?: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [lines, setLines] = useState<LineRow[]>([newLine()]);
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState<PreviewInfo | null>(null);
  const [partyLabel, setPartyLabel] = useState<string>();
  const [bankAccountLabel, setBankAccountLabel] = useState<string>();
  const tempKey = useMemo(() => crypto.randomUUID(), [open]);
  const watchedParty = Form.useWatch("partyId", form);
  const watchedBankAccount = Form.useWatch("bankAccountId", form);
  const watchedAdjustment = Form.useWatch("adjustmentMethod", form);
  const isInvoice = voucherType === "SalesInvoice" || voucherType === "PurchaseInvoice";
  const isBankVoucher = voucherType === "ReceiptVoucher" || voucherType === "PaymentVoucher";
  const pendingBills = useQuery({
    queryKey: ["pending-bills", extractId(watchedParty)],
    enabled: isBankVoucher && watchedAdjustment === "AgainstRef" && !!extractId(watchedParty),
    queryFn: () => getPendingBills<PendingBill>(extractId(watchedParty)!)
  });
  const partyBalance = useQuery({
    queryKey: ["bill-party-balance", extractId(watchedParty)],
    enabled: open && !!extractId(watchedParty),
    queryFn: () => getBillPartyBalance<BalanceInfo>(extractId(watchedParty)!)
  });
  const bankBalance = useQuery({
    queryKey: ["bill-bank-balance", extractId(watchedBankAccount)],
    enabled: open && !!extractId(watchedBankAccount),
    queryFn: () => getBillBankAccountBalance<BalanceInfo>(extractId(watchedBankAccount)!)
  });

  useEffect(() => {
    if (!open) return;
    if (!editId) {
      form.resetFields();
      form.setFieldsValue({ billDate: dayjs(), adjustmentMethod: isInvoice ? "NewRef" : "OnAccount" });
      setLines([newLine()]);
      setAllocations([]);
      setPreview(null);
      setPartyLabel(undefined);
      setBankAccountLabel(undefined);
      setDirty(false);
      return;
    }
    let active = true;
    getBillEntry<any>(editId).then((bill) => {
      if (!active) return;
      form.setFieldsValue({
        billNo: bill.billNo,
        billDate: bill.billDate ? dayjs(bill.billDate) : dayjs(),
        dueDate: bill.dueDate ? dayjs(bill.dueDate) : undefined,
        partyId: bill.partyId,
        ledgerAccountId: bill.ledgerAccountId,
        bankAccountId: bill.bankAccountId,
        sacCode: bill.sacCode,
        referenceNo: bill.referenceNo,
        adjustmentMethod: bill.adjustmentMethod ?? (isInvoice ? "NewRef" : "OnAccount"),
        amount: bill.grandTotal ?? bill.totalAmount,
        narration: bill.narration ?? bill.referenceNo
      });
      setPartyLabel(bill.partyName ?? bill.partyLabel ?? undefined);
      setBankAccountLabel(bill.bankAccountLabel ?? bill.bankAccountName ?? undefined);
      setLines((bill.lineItems?.length ? bill.lineItems : [newLine()]).map((line: any) => ({
        key: crypto.randomUUID(),
        ledgerName: line.ledgerName ?? "",
        sacCode: line.sacCode,
        quantity: line.quantity ?? 1,
        rate: line.rate ?? line.amount ?? 0,
        amount: Number(line.amount ?? 0) || (Number(line.quantity ?? 0) * Number(line.rate ?? 0)),
        per: line.per ?? "NOS",
        gstPercent: line.gstPercent ?? 0,
        tdsPercent: line.tdsPercent ?? 0,
        remarks: line.remarks
      })));
      setAllocations((bill.adjustmentEntries ?? []).map((x: any) => ({
        billId: x.againstBillId,
        allocatedAmount: x.allocatedAmount,
        remarks: x.remarks
      })).filter((x: AllocationRow) => x.billId));
      setPreview(bill);
      setDirty(false);
    }).catch(() => message.error("Unable to load voucher"));
    return () => { active = false; };
  }, [editId, form, isInvoice, message, open]);

  useEffect(() => {
    if (!open || !draft?.payload) return;
    const values = draft.payload.values ?? {};
    form.resetFields();
    form.setFieldsValue(hydrateDraftValues(values));
    setPartyLabel(values.partyLabel);
    setBankAccountLabel(values.bankAccountLabel);
    setLines(Array.isArray(draft.payload.lines) && draft.payload.lines.length ? draft.payload.lines.map((line: LineRow) => ({ ...newLine(), ...line, key: crypto.randomUUID() })) : [newLine()]);
    setAllocations(Array.isArray(draft.payload.allocations) ? draft.payload.allocations : []);
    setDirty(true);
  }, [draft, form, open]);

  const updateLine = (key: string, patch: Partial<LineRow>) => {
    setDirty(true);
    setLines((rows) => rows.map((row) => row.key === key ? { ...row, ...patch } : row));
  };

  const totals = useMemo(() => {
    const taxable = lines.reduce((sum, row) => sum + invoiceLineAmount(row), 0);
    const gst = lines.reduce((sum, row) => sum + (invoiceLineAmount(row) * Number(row.gstPercent ?? 0)) / 100, 0);
    const tds = lines.reduce((sum, row) => sum + (invoiceLineAmount(row) * Number(row.tdsPercent ?? 0)) / 100, 0);
    return { taxable, gst, tds, grand: taxable + gst - tds };
  }, [lines]);

  useEffect(() => {
    if (!open || !isInvoice || !extractId(watchedParty)) return;
    const handle = window.setTimeout(() => {
      const values = form.getFieldsValue();
      const payload = buildPayload({
        values,
        lines,
        allocations,
        isInvoice,
        isBankVoucher,
        voucherType,
        tempKey,
        title,
        fallbackTotal: totals.grand
      });
      previewBillEntry<PreviewInfo>(payload).then(setPreview).catch(() => undefined);
    }, 450);
    return () => window.clearTimeout(handle);
  }, [allocations, form, isBankVoucher, isInvoice, lines, open, tempKey, title, totals.grand, voucherType, watchedParty]);

  const save = async () => {
    const values = await form.validateFields();
    const validationError = validateVoucher(values, lines, allocations, isInvoice, isBankVoucher, totals.grand);
    if (validationError) {
      message.error(validationError);
      return;
    }
    const voucherAmount = Number(values.amount ?? totals.grand ?? 0);
    const odError = validateOverdraftPayment(voucherType, voucherAmount, bankBalance.data);
    if (odError) {
      message.error(odError);
      return;
    }

    const payload = buildPayload({
      values,
      lines,
      allocations,
      isInvoice,
      isBankVoucher,
      voucherType,
      tempKey,
      title,
      fallbackTotal: totals.grand
    });

    setSaving(true);
    try {
      if (draft) await submitDraft(draft.id, payload);
      else if (editId) await updateBillEntry(editId, payload);
      else await createBillEntry(payload);
      message.success(`${title} saved`);
      form.resetFields();
      setLines([newLine()]);
      setDirty(false);
      onSaved();
      onClose();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? "Unable to save voucher");
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (!dirty) {
      onClose();
      return;
    }
    Modal.confirm({
      title: "Discard voucher changes?",
      content: "You have unsaved changes in this voucher.",
      okText: "Discard",
      okButtonProps: { danger: true },
      onOk: onClose
    });
  };

  const saveDraft = () => {
    const values = form.getFieldsValue();
    const payload = {
      voucherType,
      values: {
        ...serializeDraftValues(values),
        partyLabel,
        bankAccountLabel
      },
      lines,
      allocations
    };
    const titleText = `${values.billDate?.format?.("YYYY-MM-DD") ?? dayjs().format("YYYY-MM-DD")} - ${title}`;
    const saveTask = draft
      ? updateDraft(draft.id, { moduleKey: voucherType as "ReceiptVoucher" | "PaymentVoucher", title: titleText, payload })
      : createDraft({ moduleKey: voucherType as "ReceiptVoucher" | "PaymentVoucher", title: titleText, payload });
    saveTask
      .then(() => {
        setDirty(false);
        onDraftSaved?.();
        message.success("Draft saved");
      })
      .catch(() => message.error("Unable to save draft"));
  };

  return (
    <Drawer title={title} open={open} onClose={requestClose} width="95vw" className="voucher-entry-drawer">
      <VoucherHeaderForm
        form={form}
        title={title}
        voucherType={voucherType}
        isInvoice={isInvoice}
        isBankVoucher={isBankVoucher}
        setDirty={setDirty}
        partyLabel={partyLabel}
        bankAccountLabel={bankAccountLabel}
        partyBalanceText={balanceText(partyBalance.data)}
        bankBalanceText={balanceText(bankBalance.data)}
        onPartyChange={(value) => setPartyLabel(value.label)}
        onBankAccountChange={(value) => setBankAccountLabel(value.label)}
      />

      {isInvoice ? (
        <VoucherInvoiceLines lines={lines} preview={preview} totals={totals} updateLine={updateLine} setLines={setLines} setDirty={setDirty} />
      ) : null}

      {isBankVoucher && watchedAdjustment === "AgainstRef" ? (
        <PendingBillsAllocationCard
          partySelected={!!extractId(watchedParty)}
          pendingBills={pendingBills}
          allocations={allocations}
          setAllocations={setAllocations}
          setDirty={setDirty}
        />
      ) : null}

      <div className="voucher-attachment-block">
        <AttachmentPanel module="BillEntry" tempKey={tempKey} compact />
      </div>

      <div className="drawer-sticky-actions">
        <Space>
          <Button onClick={saveDraft}>Save Draft</Button>
        </Space>
        <Space>
          <Button onClick={requestClose}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Save</Button>
        </Space>
      </div>
    </Drawer>
  );
}

function invoiceLineAmount(row: LineRow) {
  const explicit = Number(row.amount ?? 0);
  return explicit !== 0 ? explicit : Number(row.quantity ?? 0) * Number(row.rate ?? 0);
}
